'use client';
import React, { useState, useCallback } from 'react';
import { XMarkIcon, CameraIcon, SignalIcon, SignalSlashIcon } from '@heroicons/react/24/outline';
import { DotPerson, getDotColor, formatTimestamp, getCameraLabel } from '@/data/dotsData';

interface CameraPanelProps {
  dots: DotPerson[];
  onClose: () => void;
  onAssignDotToSlot?: (dotId: string, slotIndex: number, cameraType: 'A' | 'B') => void;
}

interface SlotCell {
  dotId: string;
  type: 'A' | 'B';
  dot: DotPerson;
}

interface SlotDetail {
  cell: SlotCell;
  slotLabel: string;
  slotIndex: number;
}

const GRID_SIZE = 8;
const TOTAL_CELLS = GRID_SIZE * GRID_SIZE;

function getSlotLabel(idx: number) {
  const row = Math.floor(idx / GRID_SIZE);
  const col = idx % GRID_SIZE;
  return `${String.fromCharCode(65 + row)}${col + 1}`;
}

export default function CameraPanel({ dots, onClose, onAssignDotToSlot }: CameraPanelProps) {
  const [selectedSlot, setSelectedSlot] = useState<SlotDetail | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  // Collect all dots that have at least one camera assigned
  const dotsWithCameras = dots.filter(d => d.cameras !== 'none');

  // Build a flat array of 64 cells
  const cells: Array<SlotCell | null> = Array(TOTAL_CELLS).fill(null);
  let cellIndex = 0;
  for (const dot of dotsWithCameras) {
    if (cellIndex >= TOTAL_CELLS) break;
    if (dot.cameras === 'A' || dot.cameras === 'both') {
      cells[cellIndex++] = { dotId: dot.dotId, type: 'A', dot };
      if (cellIndex >= TOTAL_CELLS) break;
    }
    if (dot.cameras === 'B' || dot.cameras === 'both') {
      cells[cellIndex++] = { dotId: dot.dotId, type: 'B', dot };
      if (cellIndex >= TOTAL_CELLS) break;
    }
  }

  const assignedCount = dotsWithCameras.reduce((acc, d) => {
    if (d.cameras === 'both') return acc + 2;
    return acc + 1;
  }, 0);

  const handleCellClick = useCallback((cell: SlotCell | null, idx: number) => {
    if (!cell) return;
    setSelectedSlot({ cell, slotLabel: getSlotLabel(idx), slotIndex: idx });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, idx: number, isEmpty: boolean) => {
    if (!isEmpty) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDragOverIdx(idx);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragOverIdx(null);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent, idx: number, isEmpty: boolean) => {
    e.preventDefault();
    setDragOverIdx(null);
    if (!isEmpty) return;
    const dotId = e.dataTransfer.getData('text/plain');
    if (!dotId || !onAssignDotToSlot) return;
    // Determine camera type based on slot parity: even slots → A, odd → B
    const cameraType: 'A' | 'B' = idx % 2 === 0 ? 'A' : 'B';
    onAssignDotToSlot(dotId, idx, cameraType);
  }, [onAssignDotToSlot]);

  return (
    <div className="absolute top-0 right-0 h-full w-[420px] z-[1002] flex flex-col bg-card border-l border-border shadow-2xl">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-2">
          <CameraIcon className="w-4 h-4 text-primary" />
          <span className="text-sm font-semibold text-foreground">Panel de Cámaras</span>
          <span className="text-xs bg-primary/10 text-primary rounded-full px-2 py-0.5 font-medium">
            {assignedCount} / {TOTAL_CELLS}
          </span>
        </div>
        <button
          onClick={onClose}
          className="text-muted-foreground hover:text-foreground transition-colors"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>

      {/* Hint */}
      <div className="px-4 pt-2 pb-1 shrink-0">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          Cuadrícula 8 × 8 — {TOTAL_CELLS} slots
        </p>
        <p className="text-[9px] text-muted-foreground/60 mt-0.5">
          Haz clic en un slot ocupado para ver detalles · Arrastra un punto al mapa para asignarlo
        </p>
      </div>

      {/* Slot Detail Panel */}
      {selectedSlot && (
        <SlotDetailView
          detail={selectedSlot}
          onClose={() => setSelectedSlot(null)}
        />
      )}

      {/* 8x8 Grid */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div
          className="grid gap-1"
          style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)` }}
        >
          {cells.map((cell, idx) => {
            const cellLabel = getSlotLabel(idx);
            const isDragTarget = dragOverIdx === idx;

            if (cell) {
              const isA = cell.type === 'A';
              const dotColor = getDotColor(cell.dot.lastUpdate);
              const isLive = dotColor === 'blue';
              return (
                <div
                  key={`cell-${idx}`}
                  title={`Slot ${cellLabel} — Punto #${cell.dotId} · Cámara ${cell.type}`}
                  onClick={() => handleCellClick(cell, idx)}
                  className={`
                    relative flex flex-col items-center justify-center rounded-md aspect-square text-center
                    border transition-all duration-150 cursor-pointer hover:scale-105 hover:shadow-lg
                    ${isA
                      ? 'bg-blue-500/15 border-blue-500/40 text-blue-400 hover:bg-blue-500/25' :'bg-emerald-500/15 border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/25'
                    }
                    ${selectedSlot?.slotIndex === idx ? 'ring-2 ring-white/40 scale-105' : ''}
                  `}
                >
                  {/* Live indicator dot */}
                  <span className={`absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full ${isLive ? 'bg-green-400' : 'bg-red-400'}`} />
                  {/* Camera type badge */}
                  <span className={`text-[8px] font-bold leading-none mb-0.5 ${isA ? 'text-blue-500' : 'text-emerald-500'}`}>
                    CAM {cell.type}
                  </span>
                  {/* Dot ID */}
                  <span className="text-[11px] font-bold leading-none">
                    #{cell.dotId}
                  </span>
                  {/* Slot label */}
                  <span className="text-[7px] leading-none mt-0.5 opacity-50">
                    {cellLabel}
                  </span>
                </div>
              );
            }

            return (
              <div
                key={`cell-${idx}`}
                title={`Slot ${cellLabel} — Vacío (arrastra un punto aquí)`}
                onDragOver={(e) => handleDragOver(e, idx, true)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, idx, true)}
                className={`
                  relative flex flex-col items-center justify-center rounded-md aspect-square border cursor-default
                  transition-all duration-150
                  ${isDragTarget
                    ? 'border-primary bg-primary/20 scale-105 shadow-lg shadow-primary/20'
                    : 'border-border/30 bg-muted/10'
                  }
                `}
              >
                {isDragTarget
                  ? <span className="text-[8px] text-primary font-bold leading-none">+</span>
                  : <span className="text-[7px] text-muted-foreground/30 leading-none">{cellLabel}</span>
                }
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-3 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-blue-500/20 border border-blue-500/40" />
            <span className="text-[10px] text-muted-foreground">Cámara A</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-emerald-500/20 border border-emerald-500/40" />
            <span className="text-[10px] text-muted-foreground">Cámara B</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded bg-muted/10 border border-border/30" />
            <span className="text-[10px] text-muted-foreground">Vacío</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
            <span className="text-[10px] text-muted-foreground">En vivo</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
            <span className="text-[10px] text-muted-foreground">Fuera de línea</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Slot Detail Sub-Component ────────────────────────────────────────────────
interface SlotDetailViewProps {
  detail: SlotDetail;
  onClose: () => void;
}

function SlotDetailView({ detail, onClose }: SlotDetailViewProps) {
  const { cell, slotLabel } = detail;
  const dot = cell.dot;
  const dotColor = getDotColor(dot.lastUpdate);
  const isLive = dotColor === 'blue';
  const isA = cell.type === 'A';

  return (
    <div className="mx-4 mb-2 rounded-lg border border-border bg-background/80 backdrop-blur-sm shadow-lg shrink-0">
      {/* Detail header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${isA ? 'bg-blue-500/20 text-blue-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
            CAM {cell.type}
          </span>
          <span className="text-xs font-semibold text-foreground">Slot {slotLabel}</span>
        </div>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
          <XMarkIcon className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Detail rows */}
      <div className="px-3 py-2 space-y-1.5">
        {/* Camera ID */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">ID de Cámara</span>
          <span className="text-[11px] font-mono font-semibold text-foreground">
            CAM-{cell.type}-{cell.dotId}
          </span>
        </div>

        {/* Assigned person */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">Asignado a</span>
          <span className="text-[11px] font-semibold text-foreground">
            #{dot.dotId} · {dot.name}
          </span>
        </div>

        {/* Assignment status */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">Estado de asignación</span>
          <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
            dot.cameras === 'both' ?'bg-purple-500/20 text-purple-400'
              : isA
                ? 'bg-blue-500/20 text-blue-400' :'bg-emerald-500/20 text-emerald-400'
          }`}>
            {getCameraLabel(dot.cameras)}
          </span>
        </div>

        {/* Last frame timestamp */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">Último frame</span>
          <span className="text-[10px] font-mono text-foreground/80">
            {formatTimestamp(dot.lastUpdate)}
          </span>
        </div>

        {/* Live / Offline indicator */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">Estado</span>
          <div className="flex items-center gap-1">
            {isLive ? (
              <>
                <SignalIcon className="w-3 h-3 text-green-400" />
                <span className="text-[10px] font-semibold text-green-400">En vivo</span>
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              </>
            ) : (
              <>
                <SignalSlashIcon className="w-3 h-3 text-red-400" />
                <span className="text-[10px] font-semibold text-red-400">
                  {dotColor === 'yellow' ? 'Desconectado' : 'Fuera de línea'}
                </span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
